import { isHotkey } from 'is-hotkey';
import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useDragAndSelect } from '../lib/DragAndSelectContext.js';
import useDeferred from '@fabric-app/hooks/useDeferred';
import { isEventWithActiveModifierKeys } from '@fabric-app/utils/events/isEventWithActiveModifierKeys';

const useDraggable = ({ objectId: _objectId, ref, disabled, stopPropagation, scale: _scale = 1, onDragEnd, onDragStart, objectParentId }) => {
  const [isLocalDragging, setIsLocalDragging] = useState(false);
  const [localDragState, setLocalDragState] = useState(null);
  const lastDragElements = useRef([]);
  const dragAndSelectContext = useDragAndSelect();
  const dragAndSelectContextRef = useRef(dragAndSelectContext);
  const objectId = useMemo(() => {
    return _objectId;
  }, [
    _objectId
  ]);
  const scale = useMemo(() => {
    return _scale ?? 1;
  }, [
    _scale
  ]);
  useEffect(() => {
    dragAndSelectContextRef.current = dragAndSelectContext;
  }, [
    dragAndSelectContext
  ]);
  const isDragging = useMemo(() => {
    return Boolean(!disabled && isLocalDragging || objectId && dragAndSelectContext?.isDragging && dragAndSelectContext.selectedIds.includes(objectId));
  }, [
    disabled,
    dragAndSelectContext?.isDragging,
    dragAndSelectContext?.selectedIds,
    isLocalDragging,
    objectId
  ]);
  const sendDragOut = useCallback((elements) => {
    if (!objectId || disabled) return;
    const dragOut = new CustomEvent("object-drag-out", {
      detail: {
        objectParentId,
        objectId,
        objectIds: dragAndSelectContext?.selectedIds
      }
    });
    lastDragElements.current.forEach((element) => {
      if (!elements || !elements.includes(element)) {
        element.dispatchEvent(dragOut);
      }
    });
    lastDragElements.current = elements ?? [];
  }, [
    disabled,
    dragAndSelectContext?.selectedIds,
    objectId,
    objectParentId
  ]);
  const sendDragOver = useCallback((x, y) => {
    if (!objectId || disabled) return;
    const rect = ref?.getBoundingClientRect();
    const dragOver = new CustomEvent("object-drag-over", {
      detail: {
        objectParentId,
        objectId,
        objectIds: dragAndSelectContext?.selectedIds,
        x,
        y,
        height: localDragState?.height ?? 0,
        width: localDragState?.width ?? 0,
        rect: {
          top: rect?.top ?? 0,
          left: rect?.left ?? 0,
          width: rect?.width ?? 0,
          height: rect?.height ?? 0
        }
      }
    });
    const elements = document.elementsFromPoint(x, y).filter((element2) => element2.getAttribute("data-droppable") === "true" && element2 !== ref);
    const element = elements[0];
    if (element) {
      element.dispatchEvent(dragOver);
      sendDragOut([
        element
      ]);
    } else {
      elements.forEach((element2) => {
        element2.dispatchEvent(dragOver);
      });
      sendDragOut(elements);
    }
  }, [
    ref,
    disabled,
    dragAndSelectContext?.selectedIds,
    localDragState?.height,
    localDragState?.width,
    objectId,
    sendDragOut,
    objectParentId
  ]);
  const sendDragDrop = useCallback((x, y) => {
    if (!objectId || disabled) return;
    const dragDrop = new CustomEvent("object-drag-drop", {
      detail: {
        objectParentId,
        objectId,
        objectIds: dragAndSelectContext?.selectedIds
      }
    });
    const elements = document.elementsFromPoint(x, y).filter((element2) => element2.getAttribute("data-droppable") === "true" && element2 !== ref);
    const element = elements[0];
    if (element) {
      element.dispatchEvent(dragDrop);
    } else {
      const uniqueElements = Array.from(new Set(elements));
      uniqueElements.forEach((element2) => {
        if (element2.getAttribute("data-droppable") !== "true") return;
        element2.dispatchEvent(dragDrop);
      });
    }
  }, [
    disabled,
    dragAndSelectContext?.selectedIds,
    objectId,
    ref,
    objectParentId
  ]);
  const resetTimeoutRef = useRef(null);
  const onMouseDown = (mouseDownEvent) => {
    const isModified = isEventWithActiveModifierKeys(mouseDownEvent);
    if (mouseDownEvent.button !== 0 || !ref || !objectId || disabled || isModified || mouseDownEvent.target.hasAttribute("data-non-draggable")) return;
    if (stopPropagation) {
      mouseDownEvent.stopPropagation();
    }
    const startX = mouseDownEvent.clientX;
    const startY = mouseDownEvent.clientY;
    let unlocked = false;
    let offsetX = 0;
    let offsetY = 0;
    const onMouseMove = (e) => {
      if (!ref) return;
      if ((Math.abs(e.clientX - startX) > 10 || Math.abs(e.clientY - startY) > 10) && !unlocked) {
        const rect = ref.getBoundingClientRect();
        const state = {
          width: rect.width,
          height: rect.height,
          x: rect.x,
          y: rect.y
        };
        setIsLocalDragging(true);
        onDragStart?.();
        setLocalDragState(state);
        if (dragAndSelectContext) {
          if (!dragAndSelectContext.selectedIds.includes(objectId)) dragAndSelectContext.clearSelection();
          dragAndSelectContext.setDragOriginId(objectId);
          dragAndSelectContext.setIsDragging(true);
          dragAndSelectContext.setDragState(state);
        }
        offsetX = rect.width / 2;
        offsetY = rect.height / 2;
        unlocked = true;
      }
      if (unlocked) {
        setLocalDragState((dragState) => {
          if (!dragState) return dragState;
          const newState = {
            ...dragState,
            x: e.clientX - offsetX,
            y: e.clientY - offsetY
          };
          return newState;
        });
        sendDragOver(e.clientX, e.clientY);
      }
    };
    const onMouseUp = (e) => {
      if (unlocked) {
        sendDragDrop(e.clientX, e.clientY);
      }
      onDragEnd?.();
      if (isLocalDragging && stopPropagation) {
        mouseDownEvent.stopPropagation();
      }
      if (resetTimeoutRef.current) cancelAnimationFrame(resetTimeoutRef.current);
      resetTimeoutRef.current = window.requestAnimationFrame(() => {
        setIsLocalDragging(false);
        setLocalDragState(null);
        if (dragAndSelectContext) {
          dragAndSelectContext.setDragOriginId(null);
          dragAndSelectContext.setIsDragging(false);
          dragAndSelectContext.setDragState(null);
        }
      });
      window.removeEventListener("mouseup", onMouseUp, true);
      window.removeEventListener("mousemove", onMouseMove, true);
      window.removeEventListener("keydown", onKeyDown, true);
    };
    const onKeyDown = (e) => {
      if (!isHotkey("esc", e)) return;
      e.stopPropagation();
      if (resetTimeoutRef.current) cancelAnimationFrame(resetTimeoutRef.current);
      onDragEnd?.();
      setIsLocalDragging(false);
      setLocalDragState(null);
      if (dragAndSelectContext) {
        dragAndSelectContext.setDragOriginId(null);
        dragAndSelectContext.setIsDragging(false);
        dragAndSelectContext.setDragState(null);
      }
      window.removeEventListener("mouseup", onMouseUp, true);
      window.removeEventListener("mousemove", onMouseMove, true);
      window.removeEventListener("keydown", onKeyDown, true);
    };
    window.addEventListener("mousemove", onMouseMove, true);
    window.addEventListener("mouseup", onMouseUp, true);
    window.addEventListener("keydown", onKeyDown, true);
  };
  const selectObjectIndex = useMemo(() => {
    if (!isDragging || !objectId) return -1;
    return dragAndSelectContext?.selectedIds.filter((id) => id !== dragAndSelectContext.dragOriginId).indexOf(objectId) ?? -1;
  }, [
    dragAndSelectContext?.selectedIds,
    isDragging,
    objectId,
    dragAndSelectContext?.dragOriginId
  ]);
  const draggingStyles = useMemo(() => {
    if (!isDragging || !objectId) return {
      transform: "rotate(0deg) scale(1)"
    };
    if (isLocalDragging) return {
      transform: `rotate(4deg) scale(${scale})`,
      zIndex: 100
    };
    const zIndex = -1 - selectObjectIndex;
    const indexChange = selectObjectIndex + 1;
    const rotation = indexChange * 2;
    const translateX = indexChange * 20;
    const translateY = indexChange * 15;
    return {
      transform: `rotate(-${rotation}deg) translateX(${translateX}px) translateY(${translateY}px)`,
      zIndex,
      // boxShadow: '0 0 20px 2px rgba(var(--fabric-color-text-primary-rgb), 0.2)',
      pointerEvents: "none"
    };
  }, [
    isDragging,
    objectId,
    selectObjectIndex,
    scale,
    isLocalDragging
  ]);
  const shouldRender = useMemo(() => {
    if (!dragAndSelectContext || !objectId || dragAndSelectContext.dragOriginId === objectId) return true;
    return selectObjectIndex < 3;
  }, [
    dragAndSelectContext,
    objectId,
    selectObjectIndex
  ]);
  const deferredDraggingStyles = useDeferred(draggingStyles);
  if (disabled) {
    return {
      onMouseDown: void 0,
      isDragging: false,
      dragState: null,
      draggingStyles: void 0,
      shouldRender: true,
      isDragParent: false
    };
  }
  return {
    onMouseDown,
    isDragging,
    dragState: localDragState,
    draggingStyles: deferredDraggingStyles,
    shouldRender,
    isDragParent: localDragState !== null && dragAndSelectContext?.dragOriginId === objectId
  };
};

export { useDraggable as default };
//# sourceMappingURL=useDraggable.js.map
